import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToggle } from 'react-use';
import { Common } from '@thecvlb/design-system/lib/src';

import { useLazyGetShopOrdersQuery } from 'services/shop/shop';

import PrescriptionProgress from 'features/ShippingProgress';
import ShopOrderDetails from 'modals/ShopOrderDetails';
import Loader from 'shared/Loader';

import { PathName } from 'utils/enums';

import { ShopOrderItemShortInfo } from 'models/shop';

import { PaymentMethodDetails } from './components';

const DUMMY_SHIPPING_DATA = [
  {
    completed: true,
    label: 'Processed',
    order: 0
  },
  {
    completed: false,
    label: 'Shipping',
    order: 1
  },
  {
    completed: false,
    label: 'Delivered',
    order: 2
  }
];

const Order = () => {
  const [selectedOrder, setSelectedOrder] = useState<number>(0);

  const { id = '' } = useParams<{ id: string }>();
  const orderIds = id.split(',');

  const [getShopOrders, { data: allOrders, isFetching, isError }] = useLazyGetShopOrdersQuery();

  const orders = allOrders?.data?.filter((order) => orderIds.includes(order._id));

  const navigate = useNavigate();
  const [isOpenDetailsPopup, toggleDetailsPopup] = useToggle(false);

  const getAddress = () => {
    const shippingAddress = orders?.[0]?.shippingAddress;
    if (!shippingAddress) {
      return '';
    }
    return `${shippingAddress?.address} ${shippingAddress?.city}, ${shippingAddress?.state}, ${shippingAddress?.zipCode}`;
  };

  const getPhases = (order: ShopOrderItemShortInfo) => {
    return DUMMY_SHIPPING_DATA.map((item) => {
      if (item.label === 'Shipping') {
        item.completed = ['shipping', 'shipped'].includes(order.shippingStatus?.toLowerCase());
      }
      if (item.label === 'Delivered') {
        item.completed = order.shippingStatus?.toLowerCase() === 'delivered';
      }
      return item;
    });
  };

  useEffect(() => {
    if (!!id) {
      getShopOrders();
    }
  }, [id]);

  useEffect(() => {
    if (isError) {
      navigate(PathName.Home);
    }
  }, [isError]);

  const handleOrderOnClick = (idx: number) => {
    setSelectedOrder(idx);
    toggleDetailsPopup();
  };

  return (
    <div className="flex h-full flex-col gap-6 md:mx-auto md:max-w-[420px]">
      <Loader isVisible={isFetching || isFetching} />
      <ShopOrderDetails
        data={orders?.[selectedOrder]}
        isOpen={isOpenDetailsPopup}
        onClose={toggleDetailsPopup}
      />
      <div className="flex flex-col items-center gap-8 py-6">
        <div className="flex rounded-full bg-secondary-100 p-8">
          <Common.Icon className="size-12 flex-none text-secondary" name="check" />
        </div>
        <h3 className="text-center text-m2xl font-bold md:text-2xl" data-testid="header">
          {`Your ${orders?.length && orders?.length > 1 ? 'orders have' : 'order has'} been placed`}
        </h3>
      </div>
      <div>
        {orders?.map((order, idx) => (
          <Common.Button
            className="mx-auto mb-8 mt-0"
            key={order._id}
            size="lg"
            fullWidthOnMobile
            onClick={() => handleOrderOnClick(idx)}
          >
            <div className="flex w-full flex-col items-center">
              <h3 className="mb-4 text-mBase font-bold md:text-2xl" data-testid="order_number">
                Order #{order?.orderNumber}
              </h3>
              <div className="mx-auto mb-6 w-full max-w-[310px]">
                <PrescriptionProgress phases={getPhases(order)} />
              </div>
            </div>
          </Common.Button>
        ))}
      </div>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <h2 className="text-mBase font-bold">Ship to</h2>
          <span data-testid="shipping_address">{getAddress()}</span>
        </div>
        <div className="flex flex-col gap-2">
          <h2 className="text-mBase font-bold">Payment</h2>
          <PaymentMethodDetails data={orders?.[0]?.invoice?.payment?.paymentMethod} />
        </div>
      </div>
      <div className="flex flex-col gap-3 pt-6 max-md:mt-auto">
        <Common.Button
          className="!w-full"
          color="blue"
          dataTestId="back_to_dashboard_btn"
          fullWidthOnMobile
          onClick={() => navigate(PathName.Home)}
        >
          Back To Dashboard
        </Common.Button>
        {!!orders && orders.length === 1 && (
          <Common.Button
            className="!w-full"
            color="white-alt"
            dataTestId="view_details_btn"
            fullWidthOnMobile
            onClick={toggleDetailsPopup}
          >
            View details
          </Common.Button>
        )}
      </div>
    </div>
  );
};

export default Order;
